import { useConfigure, useRefinementList } from 'react-instantsearch';
import { type FC, Fragment, useCallback, useState } from 'react';
import { ChevronRightIcon, Star } from 'lucide-react';
import { RefinementList, filterLabelClasses } from '@/components/sidebar/refinement-list';
import { isForeignCreatedAtClause, splitFilterClauses } from '@/utilities/search/filters';
import { Collapsible, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';

// The trashed toggle rewrites the whole `filters` string on every render, joining clauses with
// Typesense's ` && `. We split format-agnostically (see splitFilterClauses) and additionally drop
// any createdAt clause written in the legacy Algolia syntax, so a stale filter from a bookmarked
// URL that predates the backend flip can't survive into an invalid Typesense `filter_by`. DM-2366.
export const sanitizeFilters = (
  rawFilters: string | undefined,
  attribute: string,
  bool: boolean,
  append: boolean,
): string => {
  if (!rawFilters) {
    return append ? `${attribute}:${bool}` : '';
  }

  const decoded = decodeURIComponent(rawFilters);

  // Drop the clause being toggled and any createdAt clause authored for the other engine.
  const parts = splitFilterClauses(decoded).filter(
    (p) => !p.startsWith(`${attribute}:`) && !isForeignCreatedAtClause(p),
  );

  if (append) {
    parts.push(`${attribute}:${bool}`);
  }
  return parts.join(' && ').trim();
};

interface FilterItemProps {
  filter: {
    attribute: string;
    label: string;
    searchable?: boolean;
    searchLabel?: string;
    options?: Record<string, string>;
    fieldType?: string;
  };
}

interface BooleanFilterItemProps {
  filter: {
    attribute: string;
    label: string;
    options?: Record<string, string>;
  };
  filterOnlyFacets: Array<string>;
  setFilterOnlyFacets: (value: Array<string>) => void;
}

interface RatingFilterProps {
  filter: {
    attribute: string;
    label: string;
    options?: Array<{ value: number }>;
  };
}

export const RatingFilter: FC<RatingFilterProps> = ({ filter: { attribute, label, options } }) => {
  const tSearch = useTranslations('search');
  const { items, refine, canRefine } = useRefinementList({ attribute });

  const handleRatingChange = (value: string) => {
    refine(value);
  };

  if (!canRefine) {
    return null;
  }

  return (
    <Fragment key={label}>
      <div className="flex flex-col border-b border-ui-container px-1.5 py-0.5">
        {options?.map((opt) => {
          const match = items.find((i) => i.value === String(opt.value));
          const isRefined = match?.isRefined ?? false;
          const count = match?.count ?? 0;

          // If there is no item with a specific rating, don't show the option to filter (matches the behavior of other facets)
          if (count === 0) {
            return;
          }

          return (
            <div key={opt.value} className="flex items-center gap-x-2 py-2 pr-2 pl-4">
              <Checkbox
                id={`${attribute}-${opt.value}`}
                checked={isRefined}
                onCheckedChange={() => handleRatingChange(String(opt.value))}
                className="rounded-[3px] border-icon-base"
                data-cy={`search-facet-${attribute}-checkbox-${opt.value}`}
              />

              <label
                htmlFor={`${attribute}-${opt.value}`}
                className={cn(
                  'flex w-full items-center justify-between text-sm font-normal leading-none hover:cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
                  isRefined ? 'text-black dark:text-white' : 'text-neutral-600 dark:text-neutral-400',
                )}
              >
                <div className="flex items-center gap-x-1">
                  {opt.value > 0 ? (
                    Array.from({ length: opt.value }).map((_, i) => (
                      <Star key={i} className="icon-[18px] fill-icon-base stroke-none" />
                    ))
                  ) : (
                    <p className={filterLabelClasses}>{tSearch('noRating')}</p>
                  )}
                </div>

                <span className="ml-2" data-cy={`search-facet-${attribute}-count-${opt.value}`}>
                  {count}
                </span>
              </label>
            </div>
          );
        })}
      </div>
    </Fragment>
  );
};

export const FilterItem: FC<FilterItemProps> = ({ filter }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  // Auto-expand the section when its attribute already has an active refinement.
  const handleRefinedItemsDetected = useCallback(() => setIsExpanded(true), []);

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded} className="border-b border-ui-container px-1.5 py-0.5">
      <CollapsibleTrigger asChild data-cy={`search-facet-${filter.attribute}-second-trigger`}>
        <div className={cn('flex w-full items-center pr-1.5')}>
          <div className="mr-px cursor-pointer p-1 select-none">
            <ChevronRightIcon
              className={cn('icon-5.5 text-[#999999] transition duration-300', {
                'rotate-90': isExpanded,
              })}
            />
          </div>
          <div className="flex-1">
            <button className="flex w-full items-center justify-start py-1 outline-hidden">
              <div className="flex w-full justify-between text-sm">
                <span className="line-clamp-1 text-left font-bold break-all">{filter.label}</span>
              </div>
            </button>
          </div>
        </div>
      </CollapsibleTrigger>

      <div className={cn(isExpanded ? 'block' : 'hidden')}>
        <RefinementList
          attribute={filter.attribute}
          label={filter.label}
          searchable={filter.searchable}
          searchLabel={filter.searchLabel}
          options={filter.options}
          fieldType={filter.fieldType}
          onRefinedItemsDetected={handleRefinedItemsDetected}
        />
      </div>
    </Collapsible>
  );
};

export const BooleanFilterItem: FC<BooleanFilterItemProps> = ({ filter, setFilterOnlyFacets, filterOnlyFacets }) => {
  const checked = filterOnlyFacets.includes(filter.attribute);
  const router = useRouter();
  const { query } = router;

  useConfigure({
    filters: checked
      ? sanitizeFilters(query.filters as string, filter.attribute, false, false)
      : sanitizeFilters(query.filters as string, filter.attribute, false, true),
  });

  return (
    <Fragment key={filter.label}>
      <div className="flex items-center py-2 pr-2 pl-4">
        <Checkbox
          data-cy={`search-facet-${filter.attribute}-checkbox`}
          className="rounded-[3px] border-icon-base"
          id={`${filter.label}-${filter.attribute}`}
          value={checked ? 1 : 0}
          checked={checked}
          onCheckedChange={(val) => {
            const isChecked = Boolean(val);
            const filters = isChecked
              ? [...filterOnlyFacets, filter.attribute]
              : filterOnlyFacets.filter((attr) => attr !== filter.attribute);
            setFilterOnlyFacets(filters);
          }}
        />
        <label
          htmlFor={`${filter.label}-${filter.attribute}`}
          className={cn(
            'flex w-full items-center justify-between text-sm font-normal leading-none hover:cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
            checked ? 'text-black dark:text-white' : 'text-neutral-600 dark:text-neutral-400',
          )}
        >
          <p className={cn('ml-2', filterLabelClasses)}>{filter.label}</p>
        </label>
      </div>
    </Fragment>
  );
};
